import { useCallback, useMemo, useRef } from 'react';
import { fetchUtils, type Options } from 'ra-core';
import { useAuth } from '@clerk/react';
import { getApiUrl, getFileHostUrl, getKpiRestApiUrl } from '../api/getApiUrl';
import { useTenantId } from '../components/OrganizationGuard';
import { readJsonlStream } from './readJsonlStream';

export type HttpClientFetch = (typeof fetchUtils)['fetchJson'];
export type HttpClient = ReturnType<typeof useHttpClient>;

export const useHttpClient = () => {
  const tenantId = useTenantId();
  const { getToken } = useAuth();
  const lastRequestDate = useRef<Date | null>(null);

  const loadToken = useCallback(async () => {
    let skipCache = false;
    if (
      lastRequestDate.current &&
      lastRequestDate.current.getTime() < Date.now() - 1_000 * 60
    ) {
      // Last API request was > 60 seconds ago, so assume that the JWT is expired + get a new one
      skipCache = true;
    }
    lastRequestDate.current = new Date();

    const token = await getToken({
      skipCache,
    });

    const user = {
      authenticated: !!token,
      token: `Bearer ${token}`,
    } as const;

    return user;
  }, [getToken]);

  const sendRequest = useCallback<HttpClientFetch>(
    async (url, options) => {
      const user = await loadToken();

      return await fetchUtils.fetchJson(url, {
        ...options,
        user,
      });
    },
    [loadToken]
  );

  const api = useCallback<HttpClientFetch>(
    async (path: string, options: Options | undefined) => {
      if (!path.startsWith('/')) {
        throw new Error('Path must start with /');
      }

      const apiUrl = getApiUrl(tenantId);

      return sendRequest(`${apiUrl}${path}`, options);
    },
    [sendRequest, tenantId]
  );

  const fileHost = useCallback<HttpClientFetch>(
    async (path: string, options: Options | undefined) => {
      if (!path.startsWith('/')) {
        throw new Error('Path must start with /');
      }

      const apiUrl = getFileHostUrl(tenantId);

      return sendRequest(`${apiUrl}${path}`, options);
    },
    [sendRequest, tenantId]
  );

  const restApi = useCallback(
    async function <T>(url: string, options: Options = {}) {
      if (!url.startsWith('https://')) {
        throw new Error('URL must start with https://');
      }

      const user = await loadToken();
      const headers = options.headers || new Headers();

      if (user.authenticated) {
        if (headers instanceof Headers) {
          headers.set('Authorization', user.token);
        } else if (Array.isArray(headers)) {
          headers.push(['Authorization', user.token]);
        } else {
          headers['Authorization'] = user.token;
        }
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok || !response.body) {
        throw new Error(
          `Request failed with status ${response.status}: ${await response.text()}`
        );
      }

      return readJsonlStream<T>(response.body);
    },
    [loadToken]
  );

  const restApiKpis = useCallback(
    <T>(path: string, options: Options = {}) => {
      if (!path.startsWith('/')) {
        throw new Error('Path must start with /');
      }

      const url = getKpiRestApiUrl(tenantId, path);

      return restApi<T>(url, options);
    },
    [restApi, tenantId]
  );

  return useMemo(
    () => ({
      api,
      fileHost,
      restApiKpis,
    }),
    [api, fileHost, restApiKpis]
  );
};
